import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { toast } from 'sonner';
import type { Entity, EntityType, Link, LinkType, CanonStatus, Confidence } from '@/types/entity';
import { DIRECTED_LINK_TYPES } from '@/types/entity';
import type { BeatCoords, Project, ProjectMeta } from '@/types/project';
import type { Command, CommandKind } from '@/types/ai';
import { entitySchema, linkSchema } from '@/lib/schemas/entity.schema';
import { emptyFields } from '@/lib/schemas/entityFields';
import { extractMentionIds, isProseField, PROSE_FIELDS } from '@/lib/mentions';
import { newId } from '@/lib/id';
import { planRepoImport, type ImportPlan, type RepoTrackDto } from '@/engine/importRepoTrack';
import { db } from '@/db/codexDB';
import { loadOrSeed, seedDatabase } from '@/db/load';
import { createSnapshot } from '@/db/io/snapshot';
import type { SaveMode } from '@/components/layout/SaveStateIndicator';

const now = () => Date.now();

/** 쓰기의 단위: 영향받는 레코드의 before/after 스냅샷. null = 레코드 완전 제거. */
export interface Patch {
  entities?: Record<string, Entity | null>;
  links?: Record<string, Link | null>;
  project?: Project;
}
interface HistoryEntry {
  before: Patch;
  after: Patch;
  command: Command;
}

/** Dexie 영속 (커밋 지점). after 패치를 트랜잭션으로 기록. */
async function persistPatch(patch: Patch): Promise<void> {
  await db.transaction('rw', db.entities, db.links, db.project, async () => {
    for (const [id, e] of Object.entries(patch.entities ?? {})) {
      if (e === null) await db.entities.delete(id);
      else await db.entities.put(e);
    }
    for (const [id, l] of Object.entries(patch.links ?? {})) {
      if (l === null) await db.links.delete(id);
      else await db.links.put(l);
    }
    if (patch.project) await db.project.put(patch.project);
  });
}

function applyToDraft(draft: CodexState, patch: Patch): void {
  for (const [id, e] of Object.entries(patch.entities ?? {})) {
    if (e === null) delete draft.entities[id];
    else draft.entities[id] = e;
  }
  for (const [id, l] of Object.entries(patch.links ?? {})) {
    if (l === null) delete draft.links[id];
    else draft.links[id] = l;
  }
  if (patch.project) draft.project = patch.project;
}

const MAX_HISTORY = 200;

export interface CodexState {
  // 정규화 데이터 (UI가 읽는 인메모리 투영)
  entities: Record<string, Entity>;
  links: Record<string, Link>;
  project: Project | null;

  // 상태
  hydrated: boolean;
  saveState: SaveMode;
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];

  // 생명주기
  hydrate: () => Promise<void>;
  resetToCanon: () => Promise<void>;

  // 변경 (전부 commit 경유)
  upsertEntity: (input: Partial<Entity> & { type: EntityType; id?: string }) => string;
  patchField: (id: string, key: string, value: unknown) => void;
  patchEntity: (id: string, patch: Partial<Entity>) => void;
  removeCustomField: (id: string, key: string) => void;
  deleteEntity: (id: string) => void;
  restoreEntity: (id: string) => void;
  purgeEntity: (id: string) => void;
  addLink: (input: {
    from: string;
    to: string;
    type: LinkType;
    label?: string;
    directed?: boolean;
    canonStatus?: CanonStatus;
    confidence?: Confidence;
  }) => string;
  removeLink: (id: string) => void;
  moveBeat: (id: string, coords: BeatCoords) => void;
  reorderBeat: (id: string, order: number) => void;
  moveBeats: (moves: { id: string; storylineId: string; col: string; order: number }[]) => void;
  patchProjectMeta: (patch: Partial<ProjectMeta>) => void;
  /** K5-e 트랙 합류 — 레포 트랙(비트+전제+사실)을 한 커밋으로(undo 한 번에 전체 되돌림). */
  importRepoTrack: (dto: RepoTrackDto, placement: { afterColumnId: string }) => ImportPlan['summary'] | null;

  // 히스토리
  undo: () => void;
  redo: () => void;
}

export const useCodexStore = create<CodexState>()(
  immer((set, get) => {
    /** 단일 쓰기 경로: 낙관적 반영 → 히스토리 push → Dexie 영속 → 실패 시 rollback */
    const commit = (before: Patch, after: Patch, command: Command) => {
      set((draft) => {
        applyToDraft(draft, after);
        draft.undoStack.push({ before, after, command });
        if (draft.undoStack.length > MAX_HISTORY) draft.undoStack.shift();
        draft.redoStack = [];
        draft.saveState = 'saving';
      });
      persistPatch(after)
        .then(() => db.commands.add(command).catch(() => undefined))
        .then(() => set((d) => void (d.saveState = 'saved')))
        .catch((err) => {
          console.error('[codex] persist failed, rolling back', err);
          set((draft) => {
            applyToDraft(draft, before);
            // 실패한 커밋을 히스토리에서 제거 → undo/redo가 미저장 변경을 재적용하지 않도록
            const idx = draft.undoStack.findIndex((h) => h.command.id === command.id);
            if (idx !== -1) draft.undoStack.splice(idx, 1);
            draft.saveState = 'error';
          });
          toast.error('저장 실패 — 변경을 되돌렸습니다');
        });
    };

    const cmd = (kind: CommandKind, payload: unknown, summary?: string): Command => ({
      id: newId('cmd'),
      kind,
      payload,
      ...(summary ? { summary } : {}),
      ts: now(),
    });

    const reapply = (patch: Patch, label: 'undo' | 'redo', entry: HistoryEntry) => {
      set((draft) => {
        applyToDraft(draft, patch);
        if (label === 'undo') {
          draft.undoStack.pop();
          draft.redoStack.push(entry);
        } else {
          draft.redoStack.pop();
          draft.undoStack.push(entry);
        }
        draft.saveState = 'saving';
      });
      persistPatch(patch)
        .then(() => set((d) => void (d.saveState = 'saved')))
        .catch(() => set((d) => void (d.saveState = 'error')));
    };

    return {
      entities: {},
      links: {},
      project: null,
      hydrated: false,
      saveState: 'saved',
      undoStack: [],
      redoStack: [],

      hydrate: async () => {
        const { project, entities, links } = await loadOrSeed();
        set((d) => {
          d.project = project;
          d.entities = Object.fromEntries(entities.map((e) => [e.id, e]));
          d.links = Object.fromEntries(links.map((l) => [l.id, l]));
          d.hydrated = true;
          d.saveState = 'saved';
          d.undoStack = [];
          d.redoStack = [];
        });
      },

      resetToCanon: async () => {
        await createSnapshot('reset-to-canon');
        await seedDatabase();
        await get().hydrate();
        toast.success('바이블 v3 캐논으로 초기화되었습니다');
      },

      upsertEntity: (input) => {
        const id = input.id ?? newId(input.type);
        const existing = get().entities[id];
        const ts = now();
        const base: Entity =
          existing ??
          ({
            id,
            type: input.type,
            name: '제목 없음',
            fields: emptyFields(input.type),
            canonStatus: 'draft',
            confidence: 'probable',
            version: 0,
            createdAt: ts,
            updatedAt: ts,
          } as Entity);
        const next: Entity = {
          ...base,
          ...input,
          id,
          type: input.type,
          fields: input.fields ?? base.fields,
          version: base.version + 1,
          updatedAt: ts,
          createdAt: base.createdAt,
        };
        const parsed = entitySchema.safeParse(next);
        if (!parsed.success) {
          console.error('[codex] entity validation failed', parsed.error.issues);
          toast.error('엔티티 검증 실패');
          return id;
        }
        commit(
          { entities: { [id]: existing ?? null } },
          { entities: { [id]: next } },
          cmd('upsertEntity', { id, type: next.type }, existing ? `수정: ${next.name}` : `생성: ${next.name}`),
        );
        return id;
      },

      patchField: (id, key, value) => {
        const prev = get().entities[id];
        if (!prev) return;
        const next: Entity = {
          ...prev,
          fields: { ...prev.fields, [key]: value },
          version: prev.version + 1,
          updatedAt: now(),
        };
        // 산문 필드면 @멘션을 추출해 refs에 흡수(쓰면 그래프가 자란다 — S2 흡수 배관).
        // 엔티티의 모든 산문 필드 합집합으로 재계산(한 필드 수정이 다른 필드 멘션을 지우지 않게).
        if (isProseField(key)) {
          const ids = new Set<string>();
          for (const pf of PROSE_FIELDS) for (const mid of extractMentionIds(next.fields[pf])) ids.add(mid);
          next.refs = [...ids];
        }
        if (!entitySchema.safeParse(next).success) return;
        commit(
          { entities: { [id]: prev } },
          { entities: { [id]: next } },
          cmd('patchField', { id, key }, `${prev.name}.${key}`),
        );
      },

      patchEntity: (id, patch) => {
        const prev = get().entities[id];
        if (!prev) return;
        const next: Entity = {
          ...prev,
          ...patch,
          id,
          fields: patch.fields ? { ...prev.fields, ...patch.fields } : prev.fields,
          version: prev.version + 1,
          updatedAt: now(),
        };
        if (!entitySchema.safeParse(next).success) {
          toast.error('엔티티 검증 실패');
          return;
        }
        commit(
          { entities: { [id]: prev } },
          { entities: { [id]: next } },
          cmd('upsertEntity', { id }, `수정: ${next.name}`),
        );
      },

      removeCustomField: (id, key) => {
        const prev = get().entities[id];
        if (!prev) return;
        const { [key]: _removed, ...restFields } = prev.fields;
        void _removed;
        const next: Entity = {
          ...prev,
          fields: restFields,
          customFields: (prev.customFields ?? []).filter((f) => f.key !== key),
          version: prev.version + 1,
          updatedAt: now(),
        };
        commit(
          { entities: { [id]: prev } },
          { entities: { [id]: next } },
          cmd('upsertEntity', { id, removeField: key }, `필드 삭제: ${key}`),
        );
      },

      deleteEntity: (id) => {
        const prev = get().entities[id];
        if (!prev || prev.deletedAt) return;
        const ts = now();
        const beforeE: Record<string, Entity | null> = { [id]: prev };
        const afterE: Record<string, Entity | null> = {
          [id]: { ...prev, deletedAt: ts, version: prev.version + 1, updatedAt: ts },
        };
        // 연결된 링크 동반 soft-delete (고아 링크 방지)
        const beforeL: Record<string, Link | null> = {};
        const afterL: Record<string, Link | null> = {};
        for (const l of Object.values(get().links)) {
          if ((l.from === id || l.to === id) && !l.deletedAt) {
            beforeL[l.id] = l;
            afterL[l.id] = { ...l, deletedAt: ts, version: l.version + 1 };
          }
        }
        commit(
          { entities: beforeE, links: beforeL },
          { entities: afterE, links: afterL },
          cmd('deleteEntity', { id }, `삭제: ${prev.name}`),
        );
      },

      restoreEntity: (id) => {
        const prev = get().entities[id];
        if (!prev || !prev.deletedAt) return;
        const next = { ...prev, version: prev.version + 1, updatedAt: now() };
        delete (next as Partial<Entity>).deletedAt;
        commit(
          { entities: { [id]: prev } },
          { entities: { [id]: next } },
          cmd('restoreEntity', { id }, `복원: ${prev.name}`),
        );
      },

      purgeEntity: (id) => {
        const prev = get().entities[id];
        if (!prev) return;
        const beforeL: Record<string, Link | null> = {};
        const afterL: Record<string, Link | null> = {};
        for (const l of Object.values(get().links)) {
          if (l.from === id || l.to === id) {
            beforeL[l.id] = l;
            afterL[l.id] = null;
          }
        }
        // 다른 엔티티의 refs에 남은 purge 대상 id 청소(dangling 방지). 콘텐츠 편집이 아니므로 updatedAt은 유지.
        const beforeE: Record<string, Entity | null> = { [id]: prev };
        const afterE: Record<string, Entity | null> = { [id]: null };
        for (const e of Object.values(get().entities)) {
          if (e.id !== id && e.refs?.includes(id)) {
            beforeE[e.id] = e;
            afterE[e.id] = { ...e, refs: e.refs.filter((r) => r !== id), version: e.version + 1 };
          }
        }
        commit(
          { entities: beforeE, links: beforeL },
          { entities: afterE, links: afterL },
          cmd('deleteEntity', { id, purge: true }, `완전 삭제: ${prev.name}`),
        );
      },

      addLink: (input) => {
        const id = `link_${input.from}_${input.to}_${input.type}`;
        const link: Link = {
          id,
          from: input.from,
          to: input.to,
          type: input.type,
          ...(input.label ? { label: input.label } : {}),
          directed: input.directed ?? DIRECTED_LINK_TYPES.includes(input.type),
          canonStatus: input.canonStatus ?? 'draft',
          confidence: input.confidence ?? 'probable',
          version: 1,
        };
        if (!linkSchema.safeParse(link).success) {
          toast.error('관계 검증 실패');
          return id;
        }
        const existing = get().links[id];
        commit(
          { links: { [id]: existing ?? null } },
          { links: { [id]: link } },
          cmd('addLink', { id }, '관계 추가'),
        );
        return id;
      },

      removeLink: (id) => {
        const prev = get().links[id];
        if (!prev || prev.deletedAt) return;
        commit(
          { links: { [id]: prev } },
          { links: { [id]: { ...prev, deletedAt: now(), version: prev.version + 1 } } },
          cmd('removeLink', { id }, '관계 삭제'),
        );
      },

      moveBeat: (id, coords) => {
        const prev = get().entities[id];
        if (!prev || prev.type !== 'beat') return;
        const next: Entity = {
          ...prev,
          fields: { ...prev.fields, storylineId: coords.storylineId, col: coords.col, order: coords.order },
          version: prev.version + 1,
          updatedAt: now(),
        };
        commit(
          { entities: { [id]: prev } },
          { entities: { [id]: next } },
          cmd('moveBeat', { id, coords }, `비트 이동: ${prev.name}`),
        );
      },

      reorderBeat: (id, order) => {
        const prev = get().entities[id];
        if (!prev || prev.type !== 'beat') return;
        const next: Entity = {
          ...prev,
          fields: { ...prev.fields, order },
          version: prev.version + 1,
          updatedAt: now(),
        };
        commit(
          { entities: { [id]: prev } },
          { entities: { [id]: next } },
          cmd('reorderBeat', { id, order }, `비트 정렬: ${prev.name}`),
        );
      },

      /** 보드 드래그 결과를 한 커맨드로 일괄 반영(undo 1회). 변경된 비트만 패치. */
      moveBeats: (moves) => {
        const before: Record<string, Entity | null> = {};
        const after: Record<string, Entity | null> = {};
        const ts = now();
        for (const mv of moves) {
          const prev = get().entities[mv.id];
          if (!prev || prev.type !== 'beat') continue;
          if (
            prev.fields.storylineId === mv.storylineId &&
            prev.fields.col === mv.col &&
            Number(prev.fields.order) === mv.order
          )
            continue;
          before[mv.id] = prev;
          after[mv.id] = {
            ...prev,
            fields: { ...prev.fields, storylineId: mv.storylineId, col: mv.col, order: mv.order },
            version: prev.version + 1,
            updatedAt: ts,
          };
        }
        if (Object.keys(after).length === 0) return;
        commit(
          { entities: before },
          { entities: after },
          cmd('moveBeat', { count: Object.keys(after).length }, '비트 재배치'),
        );
      },

      patchProjectMeta: (patch) => {
        const prev = get().project;
        if (!prev) return;
        const next: Project = {
          ...prev,
          meta: { ...prev.meta, ...patch },
          updatedAt: now(),
        };
        commit({ project: prev }, { project: next }, cmd('patchProject', {}, '개요 수정'));
      },

      importRepoTrack: (dto, placement) => {
        const st = get();
        if (!st.project) return null;
        let plan: ImportPlan;
        try {
          plan = planRepoImport(dto, { project: st.project, entities: Object.values(st.entities) }, placement, now());
        } catch (err) {
          toast.error(`합류 계획 실패: ${err instanceof Error ? err.message : String(err)}`);
          return null;
        }
        for (const beat of plan.beats) {
          const parsed = entitySchema.safeParse(beat);
          if (!parsed.success) {
            console.error('[codex] import beat validation failed', beat.id, parsed.error.issues);
            toast.error(`합류 중단 — 비트 «${beat.id}» 검증 실패`);
            return null;
          }
        }
        const ts = now();
        const nextProject: Project = {
          ...st.project,
          plotColumns: plan.plotColumns,
          meta: { ...st.project.meta, engine: plan.engineMeta },
          updatedAt: ts,
        };
        const before: Patch = { project: st.project, entities: {} };
        const after: Patch = { project: nextProject, entities: {} };
        for (const beat of plan.beats) {
          before.entities![beat.id] = st.entities[beat.id] ?? null;
          after.entities![beat.id] = beat;
        }
        commit(before, after, cmd('import', { beats: plan.beats.length, asserts: plan.summary.asserts }, `레포 트랙 합류: ${dto.sequence}`));
        const unresolvedLabel = plan.summary.unresolved.length ? ` · 미해소 참조 ${plan.summary.unresolved.length}` : '';
        toast.success(`합류 완료 — 비트 ${plan.summary.beats} · 전제 ${plan.summary.asserts} · 사실 ${plan.summary.facts}${unresolvedLabel}`);
        return plan.summary;
      },

      undo: () => {
        const stack = get().undoStack;
        const entry = stack[stack.length - 1];
        if (!entry) return;
        reapply(entry.before, 'undo', entry);
      },

      redo: () => {
        const stack = get().redoStack;
        const entry = stack[stack.length - 1];
        if (!entry) return;
        reapply(entry.after, 'redo', entry);
      },
    };
  }),
);
